import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, ArrowDownRight, BarChart3, Globe, Megaphone } from 'lucide-react';
import { useDashboardData } from '~/lib/dashboard-queries';
import { useDashboardPeriod } from '~/lib/dashboard-period';
import { selectionToGadsRange } from '~/lib/gads-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { tenantCurrency, tenantHasCapability } from '~/lib/capabilities';
import { CustomerJourneyCard } from '~/components/CustomerJourneyCard/CustomerJourneyCard';
import { FunnelChart } from '~/components/FunnelChart/FunnelChart';
import { TrafficWidget } from '~/components/TrafficWidget/TrafficWidget';
import { getPlausibleSiteId } from '~/lib/analytics-queries';
import { isCfAnalyticsConfigured } from '~/lib/cf-analytics-queries';
import { GoogleAdsPanel } from '~/components/GoogleAdsPanel/GoogleAdsPanel';
import { PeriodFilter } from '~/components/PeriodFilter/PeriodFilter';
import { SourceBadge } from '~/components/Badge/Badge';
import styles from './_authed.reports.module.css';

// Sub-pestañas adaptativas: cada una aparece solo si el tenant tiene la
// capability/data correspondiente (docs/REPORTING.md). "Conversiones" es la
// base CRM-core (R1, siempre). "Analítica web" (R2/R4) gateada por
// analytics_traffic. "Google Ads" gateada por ads_google. "Campañas de correo"
// (stats Listmonk) entra después, solo cuando exista data — no como placeholder.
type ReportTab = 'conversiones' | 'trafico' | 'google_ads';

export const Route = createFileRoute('/_authed/reports')({
  validateSearch: (search: Record<string, unknown>): { tab?: ReportTab } => {
    const t = search.tab;
    if (t === 'conversiones' || t === 'trafico' || t === 'google_ads') return { tab: t };
    return {};
  },
  component: ReportsPage,
});

function computeDelta(cur: number, prev: number): { pct: number; dir: 'up' | 'down' | 'flat' } {
  if (prev === 0) return { pct: cur > 0 ? 100 : 0, dir: cur > 0 ? 'up' : 'flat' };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { pct: Math.abs(pct), dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

function ReportsPage() {
  const { selectedTenant } = useTenantContext();
  const [period, setPeriod] = useDashboardPeriod();
  const { data, isLoading } = useDashboardData({ period });
  const currency = tenantCurrency(selectedTenant);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  if (!selectedTenant) {
    return (
      <div className={styles.page}>
        <h1 className="h2">Reportes</h1>
        <div className="empty-state">Selecciona un cliente en el switcher del topbar.</div>
      </div>
    );
  }

  // Analítica web: Cloudflare si el cliente lo tiene; si no, Plausible
  // (settings.plausible.site_id). Sin ninguna de las dos, la pestaña no aparece.
  const plausibleSiteId = getPlausibleSiteId(
    selectedTenant as { settings?: { plausible?: { site_id?: string } } | null },
  );
  const cfReady = isCfAnalyticsConfigured(selectedTenant);
  const hasConversions = tenantHasCapability(selectedTenant, 'crm');
  const hasTraffic =
    tenantHasCapability(selectedTenant, 'analytics_traffic') && (cfReady || !!plausibleSiteId);
  const hasAds = tenantHasCapability(selectedTenant, 'ads_google');
  const tabs: { key: ReportTab; label: string; icon: typeof BarChart3 }[] = [
    // Conversiones es CRM-core: solo si el tenant tiene CRM. Un tenant web-cms
    // con Ads/Plausible cae directo en la primera pestaña que sí tiene.
    ...(hasConversions ? [{ key: 'conversiones' as const, label: 'Conversiones', icon: BarChart3 }] : []),
    ...(hasTraffic ? [{ key: 'trafico' as const, label: 'Analítica web', icon: Globe }] : []),
    ...(hasAds ? [{ key: 'google_ads' as const, label: 'Google Ads', icon: Megaphone }] : []),
  ];
  // Tab activa: respeta ?tab= si es válida para este tenant, sino la primera disponible.
  const activeTab: ReportTab =
    (search.tab && tabs.some((t) => t.key === search.tab) ? search.tab : tabs[0]?.key) ?? 'conversiones';
  const setTab = (key: ReportTab) =>
    navigate({ search: (prev) => ({ ...prev, tab: key }) });

  const fmtMoney = (byCurrency: Record<string, number> | undefined) => {
    const v = byCurrency?.[currency] ?? Object.values(byCurrency ?? {}).reduce((a, b) => a + b, 0);
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v || 0);
  };

  const leadsDelta = data ? computeDelta(data.leadsInPeriod, data.leadsInPrevPeriod) : null;
  const wonDelta = data ? computeDelta(data.closedWonInPeriod, data.closedWonInPrevPeriod) : null;
  const revCur = data?.revenueInPeriod?.[currency] ?? 0;
  const revPrev = data?.revenueInPrevPeriod?.[currency] ?? 0;
  const revDelta = data ? computeDelta(revCur, revPrev) : null;

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Reportes · {selectedTenant.name}</h1>
        </div>
        {/* Filtro de período global (derecha) — afecta a todas las pestañas */}
        <div className="page-header__actions">
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>
      </header>

      {/* Barra de sub-pestañas — solo si hay más de una disponible */}
      {tabs.length > 1 && (
        <nav className={styles.tabs} role="tablist" aria-label="Secciones del reporte">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                className={isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setTab(t.key)}
              >
                <Icon size={15} aria-hidden />
                {t.label}
              </button>
            );
          })}
        </nav>
      )}

      {activeTab === 'conversiones' && hasConversions && (
        <>
          {/* KPIs período-sobre-período */}
          <div className={styles.kpis}>
            <Kpi label="Oportunidades nuevas" value={data ? String(data.leadsInPeriod) : '—'}
              delta={leadsDelta} loading={isLoading} />
            <Kpi label="Ganadas" value={data ? String(data.closedWonInPeriod) : '—'} delta={wonDelta} loading={isLoading} />
            <Kpi label="Perdidas" value={data ? String(data.closedLostInPeriod) : '—'} loading={isLoading} />
            <Kpi label="Conversión" value={data?.conversionInPeriod != null ? `${Math.round(data.conversionInPeriod)}%` : '—'} loading={isLoading} />
            <Kpi label="Ingresos (ganado)" value={fmtMoney(data?.revenueInPeriod)} delta={revDelta} loading={isLoading} />
          </div>

          {/* Customer Journey — el cruce ROI */}
          <CustomerJourneyCard
            period={period}
            tenant={selectedTenant}
            leadsInPeriod={data?.leadsInPeriod ?? 0}
            qualifiedInPeriod={data?.qualifiedInPeriod ?? 0}
            qualifiedStage={data?.qualifiedStage ?? null}
            closedWonInPeriod={data?.closedWonInPeriod ?? 0}
            revenueInPeriod={data?.revenueInPeriod ?? {}}
            loading={isLoading}
          />

          <div className={styles.twoCol}>
            {/* Funnel del pipeline — mismo embudo que el Dashboard (consistencia) */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Funnel del pipeline</h2>
              <FunnelChart funnel={data?.funnel ?? []} loading={isLoading} />
            </section>

            {/* Atribución por canal — volumen + dinero (qué canal trae revenue) */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Atribución por canal</h2>
              <p className="subtle" style={{ marginTop: -4 }}>Oportunidades, ganadas e ingresos por origen · {data?.period.longLabel ?? ''}</p>
              {(data?.topSources ?? []).length === 0 ? (
                <p className={styles.muted}>Sin oportunidades en el período.</p>
              ) : (
                <table className={styles.attrTable}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontWeight: 500, color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta)', padding: '4px 0' }}>Canal</th>
                      <th className={styles.numeric} style={{ fontWeight: 500, color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta)' }}>Oport.</th>
                      <th className={styles.numeric} style={{ fontWeight: 500, color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta)' }}>Ganadas</th>
                      <th className={styles.numeric} style={{ fontWeight: 500, color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta)' }}>Ingresos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.topSources.map((s) => {
                      const rev = Object.values(s.revenue ?? {}).reduce((a, b) => a + b, 0);
                      return (
                        <tr key={s.source}>
                          <td><SourceBadge source={s.source} /></td>
                          <td className={styles.numeric}>{s.count}</td>
                          <td className={styles.numeric}>{s.won ?? 0}</td>
                          <td className={styles.numeric}>{rev > 0 ? fmtMoney(s.revenue) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <p className={styles.footnote}>Oportunidades = creadas en el período; ganadas/ingresos = cerradas en el período.</p>
            </section>
          </div>
        </>
      )}

      {activeTab === 'trafico' && hasTraffic && (
        <TrafficWidget period={period} />
      )}

      {activeTab === 'google_ads' && hasAds && (
        <GoogleAdsPanel range={selectionToGadsRange(period)} />
      )}
    </div>
  );
}

function Kpi({ label, value, delta, loading }: {
  label: string;
  value: string;
  delta?: { pct: number; dir: 'up' | 'down' | 'flat' } | null;
  loading?: boolean;
}) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue}>{loading ? '…' : value}</span>
      {delta && delta.dir !== 'flat' && (
        <span className={`${styles.kpiDelta} ${delta.dir === 'up' ? styles.up : styles.down}`}>
          {delta.dir === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {delta.pct}%
        </span>
      )}
    </div>
  );
}
